<template>
  <div class="app-layout">
    <!-- ヘッダー（/practice では非表示） -->
    <header v-if="!isPracticePage" class="app-header">
      <div class="header-content">
        <div class="logo">
          <h1>オトエイゴ</h1>
        </div>
        <!-- 兄弟が2人以上のとき: 登録名ボタンで切り替え -->
        <div v-if="hasMultipleLearners" class="learner-switcher">
          <button
            v-for="l in learners"
            :key="l.id"
            type="button"
            class="learner-btn"
            :class="{ 'learner-btn--active': selectedLearnerId === l.id }"
            @click="switchLearner(l.id)"
          >
            {{ l.name || 'お子さま' }}
          </button>
        </div>
        <nav v-if="!isManagerPage" class="header-nav">
          <router-link to="/top" class="nav-link">トップ</router-link>
          <router-link to="/mypage" class="nav-link">マイページ</router-link>
        </nav>
      </div>
    </header>

    <!-- メインコンテンツ（/practice では padding なしで動画を端まで表示） -->
    <main class="app-main" :class="{ 'app-main--no-padding': isPracticePage }">
      <router-view />
    </main>
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useSelectedLearner } from '../../composables/useSelectedLearner.js'

const route = useRoute()
const router = useRouter()
const isPracticePage = computed(() => route.path === '/practice' || route.path.startsWith('/practice/'))
const isManagerPage = computed(() => route.path.startsWith('/manager'))

const {
  learners,
  selectedLearnerId,
  hasMultipleLearners,
  loadLearners,
  setSelectedLearner
} = useSelectedLearner()

function switchLearner(learnerId) {
  setSelectedLearner(learnerId)
  router.push('/top')
}

onMounted(() => {
  loadLearners()
})
</script>

<style scoped>
.app-layout {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.app-header {
  background: #667eea;
  color: white;
  padding: 1rem 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 100;
}

.header-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.logo h1 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
}

.learner-switcher {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
}

.learner-btn {
  padding: 0.4rem 0.75rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  background: rgba(255, 255, 255, 0.15);
  color: white;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.learner-btn:hover {
  background: rgba(255, 255, 255, 0.25);
}

.learner-btn--active {
  background: rgba(255, 255, 255, 0.4);
  border-color: rgba(255, 255, 255, 0.7);
}

.header-nav {
  display: flex;
  gap: 2rem;
}

.nav-link {
  color: white;
  text-decoration: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  transition: all 0.2s ease;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  font-weight: 500;
  font-size: 1rem;
  display: inline-block;
  /* 改行防止とボタン感の向上 */
  white-space: nowrap;
  flex-shrink: 0;
  text-align: center;
  letter-spacing: 0.3px;
  touch-action: manipulation;
}

.nav-link:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.4);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.nav-link.router-link-active {
  background: rgba(255, 255, 255, 0.35);
  border-color: rgba(255, 255, 255, 0.6);
  font-weight: 600;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.nav-link.router-link-active:hover {
  background: rgba(255, 255, 255, 0.4);
  border-color: rgba(255, 255, 255, 0.7);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.test-link {
  background: #ff6b6b;
  border: 1px solid #ff5252;
}

.test-link:hover {
  background: #ff5252;
}


.app-main {
  flex: 1;
  padding: 2rem;
  background: #f8fafc;
}

.app-main.app-main--no-padding {
  padding: 0;
  margin: 0;
}

/* スマホ対応 */
@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    gap: 1rem;
  }
  
  .header-nav {
    gap: 0.5rem;
    flex-wrap: nowrap;
    justify-content: center;
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  
  .nav-link {
    padding: 0.6rem 1rem;
    font-size: 0.85rem;
    flex: 1;
    text-align: center;
    min-width: 0;
    width: 100%;
    /* 改行防止を強化 */
    white-space: nowrap;
    flex-shrink: 0;
  }
  
  .app-main:not(.app-main--no-padding) {
    padding: 1rem;
  }
}

/* より小さな画面での改行防止 */
@media (max-width: 480px) {
  .header-nav {
    gap: 0.25rem;
    padding: 0 0.5rem;
  }
  
  .nav-link {
    padding: 0.5rem 0.75rem;
    font-size: 0.8rem;
    min-width: fit-content;
  }
}
</style>
